/**
 * StoreManagementClientFactory - Returns the appropriate service client based on environment.
 *
 * Usage:
 *   const client = getServiceClient()
 *   const result = await client.listFixtures(input).toPromise()
 */

import { JwoStoreManagementGateway as Client } from '@amzn/coral_com-amazon-jwostoremanagementgateway'
import { JwoStoreManagementGatewayClientConfigFactory } from '@amzn/coral-config_jwo-store-management-gateway'
import { CoralClientFactory } from '@amzn/ihm-js-coral-client'
import { getVirgoClient } from '@amzn/jwo-store-management-platform-components'
import { AuthCoralHandler } from '@services/auth/AuthCoralHandler'
import { authService } from '@services/auth/AuthService'
import { getQualifier, getQualifierForRealm } from './UnauthenticatedClientFactory'

/**
 * Clients keyed by Coral qualifier rather than one singleton.
 *
 * The cell a session talks to is not fixed for its lifetime: it changes when a store in another
 * cell is selected, and the store lookup deliberately probes more than one cell. A single
 * singleton pinned every later call to whichever cell happened to be active on first use.
 */
const gatewayClientsByQualifier = new Map<string, Client>()

/**
 * Returns true if useVirgo URL parameter is set to true.
 */
export function shouldUseVirgo (): boolean {
    const params = new URLSearchParams(window.location.search)
    return params.get('useVirgo') === 'true'
}

/**
 * Returns the appropriate service client based on the current environment.
 * Defaults to Gateway client. Can be overridden with ?useVirgo=true URL parameter.
 *
 * TODO: Remove 'as any' and Virgo client support once Gateway is tested and Virgo is ready to be removed.
 * This is temporary to support both clients during migration.
 */
export function getServiceClient (): Client {
    if (shouldUseVirgo()) {
        return getVirgoClient() as any
    }
    return getGatewayClient()
}

/**
 * Returns a Gateway client for the cell this session should currently be talking to, creating it
 * on first use. Re-reads the active cell on every call, so a store selection that moves the
 * session to another cell takes effect immediately.
 */
export function getGatewayClient (): Client {
    return getGatewayClientForQualifier(getQualifier())
}

/**
 * Returns a Gateway client for an explicit realm, for callers that must reach a named cell rather
 * than the active one — chiefly looking a store up across cells before its own cell is known.
 */
export function getGatewayClientForRealm (realm: string | undefined): Client {
    return getGatewayClientForQualifier(getQualifierForRealm(realm))
}

function getGatewayClientForQualifier (qualifier: string): Client {
    let client = gatewayClientsByQualifier.get(qualifier)
    if (!client) {
        client = createGatewayClientForQualifier(qualifier)
        gatewayClientsByQualifier.set(qualifier, client)
    }
    return client
}

/** @internal Drops all cached clients — for testing only */
export function _resetGatewayClient (): void {
    gatewayClientsByQualifier.clear()
}

/**
 * Creates a Gateway client using the coral config factory for automatic endpoint resolution.
 */
export function createGatewayClient (): Client {
    return createGatewayClientForQualifier(getQualifier())
}

function createGatewayClientForQualifier (qualifier: string): Client {
    const handler = new AuthCoralHandler(
        () => authService.getAccessToken(),
        () => authService.getProviderConfigSync()?.clientId || null,
        {
            refreshToken: () => authService.forceRefreshAccessToken(),
            onRefreshFailed: () => authService.signOutAfterFailedRefresh(),
        },
    )
    return new CoralClientFactory(
        [JwoStoreManagementGatewayClientConfigFactory],
        qualifier,
        handler,
        [],
    ).get(Client)
}
